import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { RepricerArticlesApi } from "../../services/api/multi/repricer";
import { SalesStatsOpenButton } from "../MultiRepricer/components/ArticleSalesStatsDialog";
import {
  fmtStatsMoney,
  fmtStatsPct,
  fmtStatsQty,
} from "../MultiRepricer/hooks/useArticleSalesStats";

const BUNDLE_ART_LABELS = {
  one_to_one: "1:1 Bundle",
  value_pack: "Value Pack",
  mixed: "Mixed Bundle",
  unknown: "Bundle",
};

const BUNDLE_ART_COLORS = {
  one_to_one: "primary",
  value_pack: "secondary",
  mixed: "warning",
  unknown: "default",
};

const profitSx = { color: "success.main", fontWeight: 600 };
const profitBeforeReturnSx = { color: "success.dark", fontWeight: 600 };
const returnSx = { color: "error.main", fontWeight: 600 };

function KpiTile({ label, value, hint, valueSx }) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        minWidth: 140,
        flex: "1 1 140px",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ mt: 0.25, ...(valueSx || {}) }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

const ACTIVITY_NOTE_LABELS = {
  returns_only: "returns / costs only (no sales in period)",
  ads_only: "Otto ads, no sales in period",
  costs_only: "costs only (no sales in period)",
};

function activityNoteLabel(row) {
  const base = ACTIVITY_NOTE_LABELS[row.activity_note];
  if (!base) return null;
  if (row.activity_note === "ads_only" && row.ad_cost_applied > 0) {
    return `${base} · ads ${fmtStatsMoney(row.ad_cost_applied)}`;
  }
  return base;
}

function fmtSharePct(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(1)}%`;
}

/**
 * Expanded Main/Stock panel: direct KPIs + sales composition (no double-count).
 */
export default function AnalysisStockExpandPanel({
  sku,
  days,
  channel = "all",
  fromDate,
  toDate,
  periodLabel,
  onOpenSalesStats,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await RepricerArticlesApi.getStockConsumerStats(sku, {
          days,
          channel,
          from_date: fromDate,
          to_date: toDate,
        });
        if (!cancelled) setData(res?.data || res);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              "Failed to load stock consumers",
          );
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sku, days, channel, fromDate, toDate]);

  if (loading) {
    return (
      <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        {error}
      </Alert>
    );
  }
  if (!data) return null;

  const main = data.main || {};
  const composition = data.composition || [];
  const bundles = data.bundles || [];
  const label = periodLabel || `Last ${data.days || days} days`;
  const combinedProfit =
    data.combined_period_profit ?? data.totals?.combined_period_profit ?? 0;
  const combinedSales =
    data.combined_sales_qty ?? data.totals?.combined_sales_qty ?? null;
  const combinedRetour = data.totals?.combined_retour_qty;
  const combinedReturnCost =
    data.combined_return_cost_total ??
    data.totals?.combined_return_cost_total ??
    main.return_cost_total ??
    0;
  const combinedProfitBeforeReturnCost =
    data.combined_profit_before_return_cost ??
    data.totals?.combined_profit_before_return_cost ??
    Number(combinedProfit) + Number(combinedReturnCost);
  const combinedAdApplied =
    data.combined_ad_cost_applied ??
    data.totals?.combined_ad_cost_applied ??
    main.ad_cost_applied ??
    0;

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {data.sku}
        {data.articlename ? ` — ${data.articlename}` : ""}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={1}
      >
        Direct article analytics · {label}
        <br />
        Single sales = raw Main lines − units sold via consumer bundles (avoids
        double-counting component rows).
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Combined profit is fully net</strong> (sales margin − returned
        margin − return handling − Otto ads).
        <br />
        <strong>{fmtStatsMoney(combinedProfit)}</strong> already includes return
        costs ({fmtStatsMoney(combinedReturnCost)}) and ads (
        {fmtStatsMoney(combinedAdApplied)}). Those lines are shown for
        transparency only—not extra on top.
        <br />
        Before return costs (still after ads):{" "}
        <strong>{fmtStatsMoney(combinedProfitBeforeReturnCost)}</strong>
      </Alert>

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} mb={2}>
        <KpiTile
          label="Single sales"
          value={fmtStatsQty(main.sales_qty)}
          hint={
            data.totals?.raw_main_sales_qty != null &&
            Number(data.totals.raw_main_sales_qty) !== Number(main.sales_qty)
              ? `raw ${fmtStatsQty(data.totals.raw_main_sales_qty)} − bundles`
              : "this SKU only"
          }
        />
        <KpiTile
          label="Single revenue"
          value={fmtStatsMoney(main.gross_revenue)}
          hint="gross"
        />
        <KpiTile
          label="Single profit"
          value={fmtStatsMoney(main.period_profit)}
          hint={`net · before return costs ${fmtStatsMoney(main.profit_before_return_cost ?? Number(main.period_profit) + Number(main.return_cost_total || 0))}`}
          valueSx={profitSx}
        />
        <KpiTile
          label="Combined sales"
          value={fmtStatsQty(combinedSales)}
          hint="single + bundles"
        />
        <KpiTile
          label="Combined profit"
          value={fmtStatsMoney(combinedProfit)}
          hint="net · return costs already deducted"
          valueSx={profitSx}
        />
        <KpiTile
          label="Profit before return costs"
          value={fmtStatsMoney(combinedProfitBeforeReturnCost)}
          hint={`net profit + return costs (${fmtStatsMoney(combinedReturnCost)})`}
          valueSx={profitBeforeReturnSx}
        />
        <KpiTile
          label="Ø Sell price"
          value={fmtStatsMoney(main.avg_sell_price)}
        />
        <KpiTile
          label="Returns"
          value={fmtStatsQty(main.retour_qty)}
          hint={fmtStatsPct(main.retour_rate)}
          valueSx={returnSx}
        />
        <KpiTile
          label="Return costs"
          value={fmtStatsMoney(combinedReturnCost)}
          hint="included in profit above · not extra"
          valueSx={returnSx}
        />
        <KpiTile
          label="Ø Buy price"
          value={fmtStatsMoney(main.avg_buy_price)}
        />
        <KpiTile
          label="Ad costs"
          value={fmtStatsMoney(main.total_ad_costs)}
          hint="already net in profit"
        />
      </Stack>

      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Sales composition
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={1}
      >
        Single = raw Main sales − Σ(bundle sales × package qty). Each row shows
        that SKU&apos;s full net profit; share of combined uses the same split
        rolled into combined Main profit.
      </Typography>

      {composition.length === 0 && bundles.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          No consumer bundles reference this SKU in <code>bundle_packages</code>
          .
        </Alert>
      ) : (
        <Table
          size="small"
          sx={{ bgcolor: "background.paper", borderRadius: 1 }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Part / SKU</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Sales</TableCell>
              <TableCell align="right">SKU profit (net)</TableCell>
              <TableCell align="right">Before return costs</TableCell>
              <TableCell align="right">Share of combined</TableCell>
              <TableCell align="right">Returns</TableCell>
              <TableCell align="right">Return rate</TableCell>
              <TableCell align="right">Return costs</TableCell>
              <TableCell align="center">Stats</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {composition.map((row) => {
              const isDirect = row.part === "direct";
              const typeLabel = isDirect
                ? "Single sales"
                : BUNDLE_ART_LABELS[row.bundle_art] ||
                  row.bundle_art ||
                  "Bundle";
              return (
                <TableRow key={`${row.part}-${row.sku}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {isDirect ? "Single sales" : row.sku}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.articlename || (isDirect ? data.sku : "—")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={typeLabel}
                      color={
                        isDirect
                          ? "success"
                          : BUNDLE_ART_COLORS[row.bundle_art] || "default"
                      }
                      variant="outlined"
                    />
                    {!isDirect && row.package_qty > 1 ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        qty ×{row.package_qty}
                      </Typography>
                    ) : null}
                    {!isDirect && row.activity_note ? (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        display="block"
                      >
                        {activityNoteLabel(row)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {fmtStatsQty(row.sales_qty)}
                  </TableCell>
                  <TableCell align="right" sx={profitSx}>
                    {fmtStatsMoney(
                      isDirect ? row.attributed_profit : row.period_profit,
                    )}
                  </TableCell>
                  <TableCell align="right" sx={profitBeforeReturnSx}>
                    {fmtStatsMoney(
                      row.profit_before_return_cost ??
                        Number(
                          isDirect
                            ? row.attributed_profit
                            : row.period_profit || 0,
                        ) +
                          Number(
                            isDirect
                              ? (row.attributed_return_cost ??
                                  row.return_cost_total)
                              : row.return_cost_total || 0,
                          ),
                    )}
                  </TableCell>
                  <TableCell align="right" sx={profitSx}>
                    {fmtSharePct(row.profit_of_total_pct)}
                  </TableCell>
                  <TableCell align="right" sx={returnSx}>
                    {fmtStatsQty(row.retour_qty)}
                  </TableCell>
                  <TableCell align="right" sx={returnSx}>
                    {fmtStatsPct(row.retour_rate)}
                  </TableCell>
                  <TableCell align="right" sx={returnSx}>
                    {fmtStatsMoney(
                      isDirect
                        ? (row.attributed_return_cost ?? row.return_cost_total)
                        : row.return_cost_total,
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!isDirect && onOpenSalesStats ? (
                      <SalesStatsOpenButton
                        onClick={() =>
                          onOpenSalesStats({
                            sku: row.sku,
                            articlename: row.articlename,
                          })
                        }
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell>
                <Typography variant="body2" fontWeight={700}>
                  Combined
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stock units of this Main ≈{" "}
                  {fmtStatsQty(data.stock_units_consumed)}
                </Typography>
              </TableCell>
              <TableCell />
              <TableCell align="right">
                <strong>{fmtStatsQty(combinedSales)}</strong>
              </TableCell>
              <TableCell align="right" sx={profitSx}>
                <strong>{fmtStatsMoney(combinedProfit)}</strong>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  combined net
                </Typography>
              </TableCell>
              <TableCell align="right" sx={profitBeforeReturnSx}>
                <strong>{fmtStatsMoney(combinedProfitBeforeReturnCost)}</strong>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  before return costs
                </Typography>
              </TableCell>
              <TableCell align="right" sx={profitSx}>
                <strong>100%</strong>
              </TableCell>
              <TableCell align="right" sx={returnSx}>
                <strong>{fmtStatsQty(combinedRetour)}</strong>
              </TableCell>
              <TableCell />
              <TableCell align="right" sx={returnSx}>
                <strong>{fmtStatsMoney(combinedReturnCost)}</strong>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
